/**
 * Lighthouse Approvals API DTOs — bound to the EDR-connector routes at
 * `/api/v1/connectors/edr/lighthouse/approvals*`. Wire shape per
 * LOVABLE_APPROVALS_PAGE.md (2026-06-06 patch: every row carries a
 * live `agent` decoration).
 */

export type ApprovalState =
  | "pending"
  | "info_requested"
  | "approved"
  | "denied"
  | "expired";

export interface ApprovalAgentDecoration {
  displayName?: string;
  canonicalPublisher?: string;
  classification?: string;
  lifecycleState?: string;
  hostname?: string;
  consoleUser?: string;
  attestationManifestPath?: string;
  riskFactors?: { signals?: Record<string, number> } | null;
  riskScore?: number;
  firstSeenAt?: string;
  lastSeenAt?: string;
  /** Backend sets `false` when the underlying edr_agents row is missing. */
  found?: boolean;
}

export interface ApprovalConditions {
  expires_at?: string | null;
  only_on_devices?: string[] | null;
  max_egress_bytes_per_day?: number | null;
  [k: string]: unknown;
}

export interface ApprovalRiskSnapshot {
  totalScore?: number;
  reach?: number;
  exposure?: number;
  behavior?: number;
  posture?: number;
  signals?: Record<string, number>;
  [k: string]: unknown;
}

export interface ApprovalComment {
  id: string;
  author: string;
  kind: "comment" | "state_change" | "info_request" | "info_response" | string;
  body: string;
  createdAt: string;
}

export interface ApprovalRequestDTO {
  id: string;
  tenantId?: string;
  agentId: string;
  deviceId?: string;
  publisher?: string;
  state: ApprovalState;
  requester?: string;
  requestReason?: string;
  resolver?: string | null;
  resolutionReason?: string | null;
  conditions?: ApprovalConditions;
  riskSnapshot?: ApprovalRiskSnapshot;
  createdAt: string;
  updatedAt?: string;
  agent?: ApprovalAgentDecoration;
}

export interface ApprovalCounts {
  pending: number;
  approved: number;
  denied: number;
  infoRequested: number;
  expired: number;
}

export interface ApprovalListResponse {
  count: number;
  counts: ApprovalCounts;
  requests: ApprovalRequestDTO[];
}

export interface ApprovalDetailResponse extends ApprovalRequestDTO {
  comments?: ApprovalComment[];
}

export interface ApprovalSweepResponse {
  expired: number;
  newlyPending: number;
  agentsScanned: number;
  restrictedFound: number;
  staleCleaned: number;
  generatedAt: string;
}

export interface ApprovalHistoryResponse {
  agentId: string;
  count: number;
  requests: ApprovalRequestDTO[];
}

export const APPROVAL_STATES: ApprovalState[] = [
  "pending",
  "info_requested",
  "approved",
  "denied",
  "expired",
];

export const APPROVAL_STATE_LABEL: Record<ApprovalState, string> = {
  pending: "Pending",
  info_requested: "Info requested",
  approved: "Approved",
  denied: "Denied",
  expired: "Expired",
};
